import { useState } from 'react';
import { Briefcase, MapPin, Clock, Send, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import WaveAnimation from '../components/WaveAnimation';

interface JobListing {
  id: string;
  title: string;
  location: string;
  type: string;
  experience: string;
  overview: string;
  responsibilities: string[];
  qualifications: string[];
  benefits: string[];
  expanded: boolean;
}

export default function CareersPage() {
  const [jobListings, setJobListings] = useState<JobListing[]>([
    {
      id: 'jr-software-engineer',
      title: 'Jr. Software Engineer',
      location: 'Remote/On-Site (Dhaka)',
      type: 'Full-time',
      experience: 'Entry-Level (Fresh Graduate)',
      overview: 'As a Junior Software Engineer, you will work with senior engineers to develop, test, and optimize features for our platform. You will have the opportunity to learn and grow while working on real-world projects in AI-driven learning technologies, cloud services, and full-stack development.',
      responsibilities: [
        'Assist in developing and optimizing web and mobile applications.',
        'Work with Python, JavaScript, or relevant languages for backend and frontend development.',
        'Collaborate with senior engineers in designing and implementing scalable systems.',
        'Solve coding challenges and contribute to improving system performance.',
        'Write clean, maintainable code and participate in code reviews.',
        'Learn and integrate cloud technologies (AWS, Google Cloud, or Azure) into projects.',
        'Debug and troubleshoot technical issues efficiently.'
      ],
      qualifications: [
        'Bachelor\'s degree in Computer Science, Software Engineering, or a related field from a reputed university.',
        'Strong programming skills in Python, JavaScript, Java, or C++.',
        'Proven record of online problem-solving (e.g., LeetCode, Codeforces, HackerRank, etc.).',
        'Strong analytical and algorithmic thinking.',
        'Quick learning ability and adaptability to new technologies.',
        'Excellent communication skills and ability to work in a team.',
        'Knowledge of web frameworks (Django, Flask, React, or Node.js) is a plus.',
        'Basic understanding of databases (SQL, NoSQL) and cloud platforms.'
      ],
      benefits: [
        'Work on cutting-edge AI-powered education technology.',
        'Mentorship and career growth opportunities in a fast-paced environment.',
        'Competitive salary and benefits for fresh graduates.',
        'Hands-on experience with real-world projects and cloud-based solutions.',
        'A collaborative and learning-driven team culture.'
      ],
      expanded: false
    },
    {
      id: 'tech-lead-ai-ml',
      title: 'Tech Lead – AI/ML',
      location: 'On-Site-Flexible',
      type: 'Full-time',
      experience: 'Senior/Lead',
      overview: 'As a Tech Lead (AI/ML), you will lead the architecture, development, and deployment of machine learning models and AI services powering Tech-Learn Academy. You will work closely with data scientists, backend engineers, and product teams to build scalable, cloud-native AI solutions that enhance student engagement and learning outcomes.',
      responsibilities: [
        'Lead the design, development, and deployment of AI/ML models for personalized learning experiences.',
        'Architect and optimize ML pipelines using Python, TensorFlow, PyTorch, and Scikit-learn.',
        'Implement and manage AI/ML workloads on AWS services such as SageMaker, Bedrock, Lambda, and DynamoDB.',
        'Develop LLM-based chatbots and voice assistants for student interaction.',
        'Design AI-powered recommendation systems to personalize course content.',
        'Work with NLP, speech recognition, and computer vision technologies to enhance user experience.',
        'Ensure model performance, scalability, and security in a cloud-native environment.',
        'Collaborate with data engineers to build ETL pipelines and manage data lakes.',
        'Implement CI/CD for ML models, monitoring, and retraining workflows.',
        'Provide technical leadership, mentor junior engineers, and drive AI/ML best practices.'
      ],
      qualifications: [
        '7+ years of experience in software development, with at least 4+ years in AI/ML.',
        'Strong proficiency in Python and ML frameworks like TensorFlow, PyTorch, and Scikit-learn.',
        'Experience in deploying ML models using AWS SageMaker, Bedrock, Lambda, and API Gateway.',
        'Expertise in NLP, speech recognition, and LLM-based chatbots.',
        'Experience with Vector databases (FAISS, Pinecone, ChromaDB) and LLM fine-tuning.',
        'Strong understanding of MLOps, CI/CD for ML models, and model monitoring.',
        'Familiarity with data engineering tools (Apache Spark, Airflow, AWS Glue).',
        'Experience with Docker, Kubernetes, and serverless architectures.',
        'Ability to lead teams, make architectural decisions, and drive AI innovation.',
        'Excellent problem-solving skills and a passion for AI-driven education.'
      ],
      benefits: [
        'Work on cutting-edge AI/ML solutions shaping the future of education.',
        'Lead and mentor a high-impact AI team in an innovative learning environment.',
        'Competitive salary, benefits, and remote work flexibility.',
        'Opportunity to work with AWS AI/ML services in a production environment.'
      ],
      expanded: false
    },
    {
      id: 'senior-ios-developer',
      title: 'Senior iOS Developer',
      location: 'On-Site-Flexible',
      type: 'Full-time',
      experience: 'Senior',
      overview: 'As a Senior iOS Developer, you will be responsible for designing, developing, and maintaining our iOS applications. You will collaborate with product managers, designers, and backend developers to build a scalable and user-friendly learning platform. Your expertise in Swift, iOS frameworks, and UI/UX best practices will be critical in shaping the future of our mobile learning experience.',
      responsibilities: [
        'Lead the architecture, development, and optimization of Tech-Learn Academy\'s iOS application.',
        'Collaborate with cross-functional teams to translate business needs into functional mobile solutions.',
        'Develop and maintain reusable, scalable, and high-performance code using Swift and SwiftUI.',
        'Implement AI-powered features to enhance personalized learning experiences.',
        'Ensure the app\'s performance, quality, and responsiveness by identifying and fixing bottlenecks and bugs.',
        'Integrate APIs and third-party services to enhance app functionality.',
        'Stay updated with the latest iOS trends, tools, and best practices to continuously improve our application.',
        'Mentor junior developers and contribute to code reviews, best practices, and technical documentation.'
      ],
      qualifications: [
        '5+ years of experience in iOS app development.',
        'Strong expertise in Swift, SwiftUI, and UIKit.',
        'Proficiency in using Core Data, Combine, AVFoundation, and CloudKit.',
        'Experience with RESTful APIs, GraphQL, and third-party libraries.',
        'Knowledge of AI/ML integration for personalized learning applications is a plus.',
        'Strong understanding of mobile UI/UX best practices.',
        'Familiarity with CI/CD pipelines, automated testing, and performance optimization.',
        'Experience working with Firebase, AWS, or Google Cloud is a plus.',
        'Strong problem-solving skills and a passion for delivering high-quality software.',
        'Excellent communication and leadership skills.'
      ],
      benefits: [
        'Be part of an innovative team revolutionizing online learning.',
        'Work on cutting-edge AI-powered education technology.',
        'Competitive salary and benefits package.',
        'Flexible work environment with growth opportunities.'
      ],
      expanded: false
    }
  ]);

  const toggleJobExpansion = (id: string) => {
    setJobListings(prevListings => 
      prevListings.map(job => 
        job.id === id ? { ...job, expanded: !job.expanded } : job
      )
    );
  };

  return (
    <div className="min-h-screen bg-gray-900 pt-24 relative overflow-hidden">
      <WaveAnimation />
      
      <div className="container mx-auto px-4 py-12 relative z-10">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Join Our Team
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Help us revolutionize tech education with AI-powered learning experiences.
            Explore our open positions and become part of our innovative team.
          </p>
        </div>

        {/* Job Listings */}
        <div className="max-w-4xl mx-auto space-y-8">
          {jobListings.map(job => (
            <div 
              key={job.id}
              className="bg-gray-800/50 backdrop-blur-sm rounded-lg overflow-hidden transition-all duration-300"
            >
              {/* Job Header - Always Visible */}
              <div 
                className="p-6 cursor-pointer"
                onClick={() => toggleJobExpansion(job.id)}
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <h2 className="text-2xl font-bold text-white mb-2 flex items-center gap-2">
                      <Briefcase className="w-6 h-6 text-purple-400" />
                      {job.title}
                    </h2>
                    <div className="flex flex-wrap items-center gap-4 text-gray-300">
                      <div className="flex items-center gap-1">
                        <MapPin className="w-4 h-4 text-purple-300" />
                        <span>{job.location}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4 text-purple-300" />
                        <span>{job.type}</span>
                      </div>
                      <div className="px-3 py-1 bg-purple-600/20 rounded-full text-purple-300 text-sm">
                        {job.experience}
                      </div>
                    </div>
                  </div>
                  <button 
                    className="flex items-center gap-2 text-purple-400 hover:text-purple-300 transition-colors"
                    aria-expanded={job.expanded}
                  >
                    {job.expanded ? (
                      <>
                        <span>View Less</span>
                        <ChevronUp className="w-5 h-5" />
                      </>
                    ) : (
                      <>
                        <span>View More</span>
                        <ChevronDown className="w-5 h-5" />
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Job Details - Expandable */}
              <div 
                className={`border-t border-gray-700 transition-all duration-300 overflow-hidden ${
                  job.expanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="p-6 space-y-6">
                  {/* Overview */}
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-3">Role Overview</h3>
                    <p className="text-gray-300 leading-relaxed">{job.overview}</p>
                  </div>

                  {/* Responsibilities */}
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-3">Key Responsibilities</h3>
                    <ul className="list-disc pl-5 space-y-2 text-gray-300">
                      {job.responsibilities.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </div>

                  {/* Qualifications */}
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-3">Qualifications & Skills</h3>
                    <ul className="list-disc pl-5 space-y-2 text-gray-300">
                      {job.qualifications.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </div>

                  {/* Benefits */}
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-3">Why Join Us?</h3>
                    <ul className="list-disc pl-5 space-y-2 text-gray-300">
                      {job.benefits.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </div>

                  {/* Apply Button */}
                  <div className="pt-4">
                    <a 
                      href={`mailto:recruitme@tech-learn.academy?subject=Application for ${job.title} Position&body=Dear Hiring Team,%0D%0A%0D%0AI am writing to apply for the ${job.title} position at Tech Learn Academy.%0D%0A%0D%0AJob Title: ${job.title}%0D%0AYears of Experience: [Your experience]%0D%0ACurrent Organization: [Your current company]%0D%0A%0D%0A[Your message]%0D%0A%0D%0AThank you for considering my application.%0D%0A%0D%0ASincerely,%0D%0A[Your Name]`}
                      className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-colors"
                    >
                      <Send className="w-5 h-5" />
                      Apply for this Position
                    </a>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Application Process */}
        <div className="max-w-4xl mx-auto mt-16 bg-gradient-to-br from-purple-900/30 to-gray-800/30 backdrop-blur-sm rounded-lg p-8">
          <h2 className="text-2xl font-bold text-white mb-6">How to Apply</h2>
          <div className="space-y-4 text-gray-300">
            <p>
              To apply for any of the positions listed above, please send your resume and cover letter to:
              <a 
                href="mailto:recruitme@tech-learn.academy" 
                className="text-purple-400 hover:text-purple-300 ml-2"
              >
                recruitme@tech-learn.academy
              </a>
            </p>
            <p>Please include the following information in your email:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Job Title you're applying for</li>
              <li>Years of relevant experience</li>
              <li>Current Organization (if applicable)</li>
              <li>A brief introduction about yourself and why you're interested in joining Tech Learn Academy</li>
            </ul>
            <p className="mt-6">
              We review all applications carefully and will contact qualified candidates for the next steps in the interview process.
            </p>
          </div>
        </div>

        {/* Join Our Team CTA */}
        <div className="max-w-4xl mx-auto mt-16 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Don't see a position that fits your skills?</h2>
          <p className="text-gray-300 mb-6">
            We're always looking for talented individuals to join our team. Send your resume to 
            <a 
              href="mailto:recruitme@tech-learn.academy" 
              className="text-purple-400 hover:text-purple-300 mx-1"
            >
              recruitme@tech-learn.academy
            </a> 
            and let us know how you can contribute to our mission.
          </p>
          <a 
            href="https://www.linkedin.com/company/techlearnacademy" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-purple-400 hover:text-purple-300"
          >
            Follow us on LinkedIn <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>
    </div>
  );
}